import React, { useCallback } from 'react'
import useCruisePurchaseDetails from 'hooks/Cruise/useCruisePurchaseDetails'
import TextButton from 'LuxKit/Button/TextButton'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import { pluralizeToString } from 'lib/string/pluralize'
import BodyText from 'LuxKit/Typography/BodyText'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'

interface Props {
  order: App.Order
  isPastOrder: boolean
  bookingId: string
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
}

function LesaCruisePurchaseSummary(props: Props) {
  const { order, isPastOrder, bookingId, hideSelect, actions, payload, componentsMap } = props

  const dispatch = useAppDispatch()

  const {
    name,
    bookingNumber,
    travelers,
    cabinInfo,
    cruiseItem,
    booking,
    offerSnapshot,
  } = useCruisePurchaseDetails(order, bookingId)

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id } }))
  }, [dispatch, order.id])

  if (!booking || !offerSnapshot) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'showFinePrint', 'resendConfirmationEmail']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineShipIcon size="XS" />}>Cruise</BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >
                {!offerSnapshot.isSamePort && <>
                  Starts in <b>{offerSnapshot.departurePortName}</b>,
                  Ends in <b>{offerSnapshot.returnPortName}</b>
                </>}
                {offerSnapshot.isSamePort && <>
                  Starts and ends in{' '}
                  <b>{offerSnapshot.departurePortName}</b>.
                </>}
              </CaptionTextBlock>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {name}
              </LesaPurchaseSummaryTitle>{' '}
              <LesaPurchaseSummarySeparator>
                <BodyText as="span" variant="small" colour="neutral-two">{offerSnapshot.duration} days</BodyText>
                <BodyText as="span" variant="small" colour="neutral-two">{offerSnapshot.numberOfCities} cities</BodyText>
                {!!offerSnapshot.numberOfCountries &&
                  <BodyText as="span" variant="small" colour="neutral-two">
                    {offerSnapshot.numberOfCountries} countries
                  </BodyText>}
              </LesaPurchaseSummarySeparator>
              <LesaPurchaseSummaryItineraryDates
                status={cruiseItem.status}
                showReservationDates={!!offerSnapshot.departureDate}
                startDate={offerSnapshot.departureDate}
                endDate={offerSnapshot.returnDate}
                bookingNumber={bookingNumber}
              />
              <LesaPurchaseSummarySeparator>
                {!!travelers?.numberOfAdults && (
                  <>
                    <BodyText as="span" variant="small" colour="neutral-three">{pluralizeToString('adult', travelers.numberOfAdults)}{!!(travelers.numberOfChilds) && `, ${pluralizeToString('child', travelers.numberOfChilds)}`}</BodyText>
                    <BodyText as="span" variant="small" colour="neutral-three">{cabinInfo?.cabinType}</BodyText>
                  </>
                )}
              </LesaPurchaseSummarySeparator>
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                Select
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}

export default LesaCruisePurchaseSummary
